import { SelectorOption } from '@/types/Configurator/SelectorOption';
import {
  ConfigurableCollectionProductObject,
  ProductOptionCategoryProductOptionObject,
} from '@/types/joostconnector';
import { useScrollLock } from '@vueuse/core';
import _ from 'lodash';
import { defineStore } from 'pinia';
import { computed, ComputedRef, ref, Ref, watch } from 'vue';
import { useExtraOptionsStore } from '../ExtraOptionsStore';

export const useCollectionStore = defineStore(
  `collection-${window.product.sku}`,
  () => {
    const extraOptionsStore = useExtraOptionsStore();

    /**
     * Map size value object to SelectorOption
     * @param endProduct
     */
    function endProductToDropDown(
      endProduct: ConfigurableCollectionProductObject | undefined
    ): SelectorOption | undefined {
      if (!endProduct) {
        return undefined;
      }

      return {
        label: endProduct.webshopName,
        value: endProduct.sku,
        price: endProduct.price ?? undefined,
        isDefault: endProduct.isDefault ?? false,
        recommendedPrice: endProduct.recommendedPrice ?? undefined,
        localizedRecommendedPrice:
          endProduct.localizedRecommendedPrice ?? undefined,
        localizedPrice: endProduct.localizedPrice ?? undefined,
      };
    }

    const endProducts: ComputedRef<ConfigurableCollectionProductObject[]> =
      computed(() =>
        _.sortBy(window.end_products, [
          'productAttributes.width',
          'productAttributes.height',
        ])
      );

    const endProductOptions: ComputedRef<SelectorOption[]> = computed(
      () =>
        _.filter(
          _.map(endProducts.value, endProductToDropDown)
        ) as SelectorOption[]
    );

    const selectedEndProductOption: Ref<SelectorOption | undefined> = ref(
      endProductToDropDown(endProducts.value[0])
    );

    /**
     * Selected size for collections
     */
    const selectedEndProductValue: ComputedRef<
      ConfigurableCollectionProductObject | undefined
    > = computed(
      () =>
        _.find(
          endProducts.value,
          (endProduct: ConfigurableCollectionProductObject) =>
            endProduct.sku === selectedEndProductOption.value?.value
        ) ?? endProducts.value[0]
    );

    const extraOptionsModalOpen: Ref<boolean> = ref(false);

    const productPrice: ComputedRef<number> = computed(() =>
      Math.round(selectedEndProductValue.value?.price ?? 0)
    );
    const productRecommendedPrice: ComputedRef<number> = computed(
      () => selectedEndProductValue.value?.recommendedPrice ?? 0
    );
    const optionPrices: ComputedRef<number> = computed(() =>
      _.sumBy(_.map(extraOptionsStore.selectedOptions, 'price'))
    );
    const optionRecommendedPrices: ComputedRef<number> = computed(() =>
      _.sumBy(_.map(extraOptionsStore.selectedOptions, 'recommendedPrice'))
    );
    const totalRecommendedPrice: ComputedRef<number> = computed(
      () =>
        (productRecommendedPrice.value > 0
          ? productRecommendedPrice.value
          : productPrice.value) +
        optionWithoutRecommendedPricePrices.value +
        optionRecommendedPrices.value
    );
    const optionWithoutRecommendedPricePrices: ComputedRef<number> = computed(
      () =>
        _.sumBy(
          _.filter(
            extraOptionsStore.selectedOptions,
            (extraOption: ProductOptionCategoryProductOptionObject) =>
              !extraOption.recommendedPrice
          ),
          'price'
        )
    );

    const totalPrice: ComputedRef<number> = computed(
      () => productPrice.value + optionPrices.value
    );

    const totalSavedPrice: ComputedRef<number> = computed(
      () => totalRecommendedPrice.value - totalPrice.value
    );

    const previousSku: Ref<string | number | undefined> = ref(
      selectedEndProductOption.value?.value
    );

    const bodyElement: Ref<HTMLElement> = ref(document.body);
    const isBackgroundScrollLocked: Ref<boolean> = useScrollLock(bodyElement);

    function lockBackgroundScrolling(): void {
      isBackgroundScrollLocked.value = true;
      document.body.style.overflowY = 'hidden';
      document.body.style.touchAction = 'none';
      document.body.style.overscrollBehavior = 'none';
    }

    function unlockBackgroundScrolling(): void {
      isBackgroundScrollLocked.value = false;
      document.body.style.overflowY = 'auto';
      document.body.style.touchAction = 'auto';
      document.body.style.overscrollBehavior = 'auto';
    }

    watch(
      () => extraOptionsModalOpen.value,
      (extraOptionsModalOpen: boolean) =>
        extraOptionsModalOpen
          ? lockBackgroundScrolling()
          : unlockBackgroundScrolling()
    );

    return {
      endProducts,
      endProductOptions,
      selectedEndProductOption,
      selectedEndProductValue,
      extraOptionsModalOpen,
      totalPrice,
      optionRecommendedPrices,
      optionWithoutRecommendedPricePrices,
      optionPrices,
      productPrice,
      productRecommendedPrice,
      totalRecommendedPrice,
      totalSavedPrice,
      previousSku,
      isBackgroundScrollLocked,
    };
  },
  {
    persist: {
      paths: ['selectedEndProductOption', 'selectedEndProductValue'],
      storage: sessionStorage,
    },
  }
);
